import React from 'react';
import { RenderRequest, DynamicRenderResponse } from 'types/cx-editor';
import { useGlobalConfig } from 'context/GlobalContext';
import { createCxApiClient } from 'framework/http/cxEditorClient';
import { useDebounceEffect } from 'hooks/useDebounceEffect';
import { PreviewFrame } from './PreviewFrame';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { parseHtmlSections, filterHtmlBySection, getSectionIdFromElementKey } from 'utils/html-section-parser';
import { resolveScenarioId } from 'utils/cx-editor';

type Props = { 
  request: RenderRequest | null; 
  rightRequest?: RenderRequest | null;
  previewMode?: 'desktop' | 'mobile';
  channel?: string;
  isFullTemplateSelected?: boolean;
  projectId?: string | null;
  elementKey?: string; // Current selected element/section
  onSectionsParsed?: (sections: Array<{ elementKey: string; displayName: string }>) => void; // Callback to update element dropdown
  onRenderComplete?: (templateKey: string) => void; // Callback when render completes to trigger template/scenario fetch
  onHtmlChange?: (html: string) => void; // Callback to expose rendered HTML to parent
};

// Module-level in-flight tracking to prevent duplicate calls across component instances and StrictMode re-renders
const inFlightRenders = new Set<string>();

export const PreviewArea: React.FC<Props> = ({ 
  request, 
  rightRequest,
  previewMode = 'desktop',
  channel = 'Email',
  isFullTemplateSelected = false,
  projectId,
  elementKey = 'Full Template',
  onSectionsParsed,
  onRenderComplete,
  onHtmlChange,
}) => {
  const { rc } = useGlobalConfig();
  const selectedProjectId = useSelector(selectSelectedPilotId);
  const effectiveProjectId = projectId || selectedProjectId;
  
  const [fullHtml, setFullHtml] = React.useState<string>(''); // Store full rendered HTML
  const [html, setHtml] = React.useState<string>(''); // Filtered HTML based on elementKey
  const [loading, setLoading] = React.useState(false);
  const [subject, setSubject] = React.useState<string>('');
  const [htmlB, setHtmlB] = React.useState<string>('');
  const [loadingB, setLoadingB] = React.useState(false);
  const [subjectB, setSubjectB] = React.useState<string>('');
  
  // Filter HTML when elementKey changes (client-side filtering, no API call)
  React.useEffect(() => {
    if (!fullHtml) {
      setHtml('');
      return;
    }
    
    if (elementKey === 'Full Template') {
      setHtml(fullHtml);
    } else {
      // Get section ID from element key, verifying it exists in the HTML
      const sectionId = getSectionIdFromElementKey(elementKey, fullHtml);
      if (sectionId) {
        const filtered = filterHtmlBySection(fullHtml, sectionId);
        setHtml(filtered);
      } else {
        // Section not found, show full HTML with warning
        console.warn(`[PreviewArea] Section ID not found for element key "${elementKey}", showing full template`);
        setHtml(fullHtml);
      }
    }
  }, [elementKey, fullHtml]);

  // Extract primitive values from request to use as stable dependencies
  const templateKey = request?.templateKey || null;
  const scenario = request?.variant?.scenario || null;
  const hierarchy = request?.variant?.hierarchy || null;
  const locale = request?.variant?.locale || null;
  const refreshKey = request?.refreshKey ?? null;
  
  // API call only when template, scenario, or refreshKey changes (NOT when elementKey changes)
  useDebounceEffect(() => {
    // Check and mark as in-flight SYNCHRONOUSLY (before async) to prevent race conditions
    if (!request || !effectiveProjectId || !templateKey || !scenario) {
      setFullHtml(''); 
      setHtml('');
      setSubject('');
      return;
    }
    
    // Create a unique key for this request (include refreshKey to force refresh)
    const requestKey = `${templateKey}-${scenario}-${effectiveProjectId}-${refreshKey ?? 0}`;
    
    // Prevent duplicate calls - check SYNCHRONOUSLY before async function
    // This prevents race conditions when StrictMode causes double renders
    if (inFlightRenders.has(requestKey)) {
      console.log('[PreviewArea] Request already in flight, skipping duplicate:', requestKey, {
        inFlightSet: Array.from(inFlightRenders),
        templateKey,
        scenario
      });
      return;
    }
    
    // Mark as in-flight SYNCHRONOUSLY (module-level, persists across re-renders)
    inFlightRenders.add(requestKey);
    console.log('[PreviewArea] Marked request as in-flight:', requestKey);
    
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        
        // Resolve "default" scenario to actual scenario ID from data scenarios API
        const resolvedScenarioId = await resolveScenarioId(
          scenario || 'default',
          templateKey || '',
          channel || 'Email',
          () => api.getDataScenarios()
        );
        
        // Note: resolvedScenarioId is the actual scenario ID (e.g., "MS_1", "MS_2"), not "default"
        console.log('[PreviewArea] Calling renderDynamic API', {
          projectId: effectiveProjectId,
          originalScenario: scenario,
          resolvedScenarioId, // This is the actual scenario ID sent to API
          templateKey: templateKey,
          requestKey,
          apiUrl: process.env.REACT_APP_TOOL_API_URL
        });
        
        const resp: DynamicRenderResponse = await api.renderDynamic({
          projectId: effectiveProjectId,
          scenarioId: resolvedScenarioId, // Sending resolved scenario ID (e.g., "MS_1") to API
          uuid: '' // UUID can be ignored or passed empty
        });
        console.log('[PreviewArea] API response received', { 
          hasHtml: !!resp.html, 
          hasSubject: !!resp.subject,
          htmlLength: resp.html?.length,
          requestKey
        });
        if (active && inFlightRenders.has(requestKey)) {
          // Clear in-flight flag
          inFlightRenders.delete(requestKey);
          
          const renderedHtml = resp.html || '';
          setFullHtml(renderedHtml); // Store full HTML
          setSubject(resp.subject || '');
          
          // Expose HTML to parent (for InspectorPanel to get technical section IDs)
          if (onHtmlChange && renderedHtml) {
            onHtmlChange(renderedHtml);
          }
          
          // Parse sections from HTML and notify parent
          if (renderedHtml && onSectionsParsed) {
            const { elementKeys } = parseHtmlSections(renderedHtml);
            // Create elements array with "Full Template" as first option
            const sections = [
              { elementKey: 'Full Template', displayName: 'Full Template' },
              ...elementKeys.map(key => ({ elementKey: key, displayName: key }))
            ];
            onSectionsParsed(sections);
          }
          
          // Notify parent that render is complete - trigger template/scenario fetch
          if (onRenderComplete && templateKey) {
            onRenderComplete(templateKey);
          }
          
          // Apply current element filter
          if (elementKey === 'Full Template') {
            setHtml(renderedHtml);
          } else {
            const sectionId = getSectionIdFromElementKey(elementKey, renderedHtml);
            if (sectionId) {
              setHtml(filterHtmlBySection(renderedHtml, sectionId));
            } else {
              // Section not found, show full HTML
              setHtml(renderedHtml);
            }
          }
        }
      } catch (err) {
        console.error('[PreviewArea] API call failed', err);
        if (active && inFlightRenders.has(requestKey)) {
          inFlightRenders.delete(requestKey);
          const errorHtml = '<div style="padding:12px;color:crimson">Failed to render preview</div>';
          setFullHtml(errorHtml);
          setHtml(errorHtml);
          setSubject('');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();
    return () => { 
      active = false;
      // Don't clear inFlightRef here - let the request complete naturally
    };
  }, 400, [templateKey, scenario, effectiveProjectId, refreshKey]); // Use primitive values, include refreshKey to trigger refresh after save

  // Extract primitive values from rightRequest for stable dependencies
  const rightTemplateKey = rightRequest?.templateKey || null;
  const rightScenario = rightRequest?.variant?.scenario || null;
  
  useDebounceEffect(() => {
    let active = true;
    (async () => {
      if (!rightRequest || !effectiveProjectId || !rightTemplateKey || !rightScenario) { 
        setHtmlB(''); 
        setSubjectB('');
        return; 
      }
      
      // Create a unique key for this request
      const requestKey = `${rightTemplateKey}-${rightScenario}-${effectiveProjectId}-right`;
      
      // Prevent duplicate calls
      if (inFlightRenders.has(requestKey)) {
        console.log('[PreviewArea] Right request already in flight, skipping duplicate:', requestKey);
        return;
      }
      
      // Mark as in-flight
      inFlightRenders.add(requestKey);
      
      setLoadingB(true);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        
        // Resolve "default" scenario to actual scenario ID from data scenarios API
        const resolvedScenarioId = await resolveScenarioId(
          rightScenario || 'default',
          rightTemplateKey || '',
          channel || 'Email',
          () => api.getDataScenarios()
        );
        
        const resp: DynamicRenderResponse = await api.renderDynamic({
          projectId: effectiveProjectId,
          scenarioId: resolvedScenarioId, // Sending resolved scenario ID to API
          uuid: '' // UUID can be ignored or passed empty
        });
        if (active && inFlightRenders.has(requestKey)) {
          inFlightRenders.delete(requestKey);
          setHtmlB(resp.html || '');
          setSubjectB(resp.subject || '');
        }
      } catch (err) {
        if (active && inFlightRenders.has(requestKey)) {
          inFlightRenders.delete(requestKey);
          setHtmlB('<div style="padding:12px;color:crimson">Failed to render preview</div>');
          setSubjectB('');
        }
      } finally {
        if (active) setLoadingB(false);
      }
    })();
    return () => { active = false; };
  }, 400, [rightTemplateKey, rightScenario, effectiveProjectId]);
  if (rightRequest) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1px 1fr',
          gap: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          overflow: 'hidden'
        }}
      >
        <PreviewFrame
          variantName="A"
          variant={null}
          renderRequest={request}
          previewMode={previewMode}
          channel={channel}
          isFullTemplateSelected={isFullTemplateSelected}
          renderedHtml={html}
          renderedSubject={subject}
          loading={loading}
        />
        <Box
          sx={{
            width: '1px',
            bgcolor: 'divider'
          }}
        />
        <PreviewFrame
          variantName="B"
          variant={null}
          renderRequest={rightRequest}
          previewMode={previewMode}
          channel={channel}
          isFullTemplateSelected={isFullTemplateSelected}
          renderedHtml={htmlB}
          renderedSubject={subjectB}
          loading={loadingB}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: 2,
        p: 2,
        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        overflow: 'auto'
      }}
    >
      <PreviewFrame
        variant={null}
        renderRequest={request}
        previewMode={previewMode}
        channel={channel}
        isFullTemplateSelected={isFullTemplateSelected}
        renderedHtml={html}
        renderedSubject={subject}
        loading={loading}
      />
    </Box>
  );
};
